import { useRef } from 'react'
import { useRetourDuFocus } from '@/platform/hooks/useRetourDuFocus'
import { Button } from '@/platform/components/ui/button'
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '@/platform/components/ui/dialog'

/**
 * Spec 160 (FR-023, research D-160-12) — confirmer avant de demander ou de lancer un audit. Les deux gestes partaient au
 * premier clic ; lancer un audit interroge réellement l'organisation Microsoft 365 du client et produit un relevé.
 *
 * Contrôlée : l'appelant tient `ouverte` et agit dans `onConfirmer`. « Annuler » a le focus : un Entrée réflexe ne lance
 * rien. Échap, le clic hors de la boîte et la croix valent « Annuler ».
 */
export function ConfirmationAudit({ ouverte, nature, nomClient, onConfirmer, onAnnuler }: {
  ouverte: boolean
  nature: 'demande' | 'lancement'
  nomClient: string
  onConfirmer: () => void
  onAnnuler: () => void
}) {
  const annuler = useRef<HTMLButtonElement>(null)
  // Fermée, la boîte rend le focus à l'élément qui l'avait à l'ouverture (bouton, menu de la date) — pas à la page.
  const retour = useRetourDuFocus()
  const titre = nature === 'demande' ? `Demander un audit pour ${nomClient} ?` : `Lancer l'audit de ${nomClient} ?`
  const texte = nature === 'demande'
    ? 'La demande sera transmise à Pragmelis ; vous serez informé de la suite.'
    : `L'audit interroge l'organisation Microsoft 365 de ${nomClient} et produit un nouveau relevé.`

  return (
    <Dialog open={ouverte} onOpenChange={o => { if (!o) onAnnuler() }}>
      <DialogContent
        onOpenAutoFocus={e => { retour.memoriser(); e.preventDefault(); annuler.current?.focus() }}
        onCloseAutoFocus={retour.rendre}
      >
        <DialogHeader>
          <DialogTitle>{titre}</DialogTitle>
        </DialogHeader>
        <DialogDescription className="px-6 py-4">{texte}</DialogDescription>
        <DialogFooter>
          <Button ref={annuler} variant="outline" onClick={onAnnuler}>Annuler</Button>
          <Button onClick={onConfirmer}>Confirmer</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
